import React, { use, useCallback, useMemo, useState } from "react";
import { ScrollView } from "react-native";
import { useI18n } from "../../hooks/i18n-hooks";
import { useMutation } from "@apollo/client/react";
import { AuthManagerContext, AuthStatus } from "../../contexts/AuthManager";
import TextField from "../../components/TextField";
import VStack from "../../ui_atoms/VStack";
import ScreenCard from "../ScreenCard";
import { useApolloErrorSnackbar } from "../../hooks/snackbar";
import { UPDATE_USER } from "../../graphql/identity";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";

type EditorFields = {
    email: string;
};

type ValidationErrors = Record<keyof EditorFields, string>;

type AccountEditorScreenProps = StackScreenProps<RootStackParamList, "AccountEdit">;

const AccountEditorScreen: React.FC<AccountEditorScreenProps> = ({ navigation }) => {
    const { user, status, refreshUser } = use(AuthManagerContext);
    const [updateUser, { error: saveError, loading: saving, reset }] = useMutation(UPDATE_USER);
    useApolloErrorSnackbar(saveError, reset);

    const loading = status == AuthStatus.Loading;
    const i18n = useI18n();

    const [email, setEmail] = useState<string>(user?.email || "");

    const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
        {} as ValidationErrors,
    );

    // Resync local email when the upstream user changes. Uses the "adjust state
    // on prop change" pattern.
    const [prevUser, setPrevUser] = useState(user);
    if (user !== prevUser) {
        setPrevUser(user);
        if (user && email !== (user.email || "")) {
            setEmail(user.email || "");
        }
    }

    const validate = useCallback((): boolean => {
        const result: Partial<ValidationErrors> = {};

        if (!email) {
            result.email = i18n.t("screens.user.errors.email");
        }

        setValidationErrors(result as ValidationErrors);
        return Object.keys(result).length == 0;
    }, [email, i18n]);

    const save = React.useCallback(async () => {
        if (!validate()) {
            return;
        }
        try {
            await updateUser({
                variables: {
                    userData: {
                        email,
                    },
                },
            });
            void refreshUser?.(); // do not await to avoid visual glitch
            navigation.goBack();
        } finally {
            // this is handled by saveError in useMutation
        }
    }, [validate, updateUser, email, refreshUser, navigation]);

    const hasChanges = useMemo(() => {
        return user?.email !== email;
    }, [email, user?.email]);

    const hasError = useMemo(() => {
        return Object.keys(validationErrors).length !== 0;
    }, [validationErrors]);

    const disabled = loading || saving;

    return (
        <ScreenCard
            buttons={[
                {
                    title: i18n.t("save"),
                    onPress: save,
                    validating: saving,
                    isValid: hasChanges && !hasError,
                },
            ]}
        >
            <ScrollView>
                <VStack v={1} gap={1} alignment="stretch" h="2/3">
                    <TextField
                        error={validationErrors.email}
                        value={email}
                        label={validationErrors.email || i18n.t("user.email")}
                        onChangeText={setEmail}
                        keyboardType="email-address"
                        textContentType="emailAddress"
                        autoComplete="email"
                        autoCorrect={false}
                        autoCapitalize="none"
                        disabled={disabled}
                    />
                </VStack>
            </ScrollView>
        </ScreenCard>
    );
};

export default AccountEditorScreen;
